/**
 * TD UTM Tracking Web Component
 * 
 * Captures UTM parameters from the URL and stores them in session storage.
 * Also populates hidden form inputs for passing UTM data as order attributes.
 */

if (!customElements.get('td-utm-tracking')) {
	class TdUtmTracking extends HTMLElement {
		constructor() {
			super();
			this.UTM_STORAGE_PREFIX = 'order_attribute_';
			this.UTM_PARAMS = ['utm_source', 'utm_campaign', 'utm_medium'];
			this.urlParams = new URLSearchParams(window.location.search);
			
			// Bind handlers for cleanup
			this.handleDOMContentLoaded = this.populateFormInputs.bind(this);
			this.handleCartDrawerRendered = this.populateFormInputs.bind(this);
		}

		connectedCallback() {
			this.storeUTMParameters();
			
			// Handles race condition where the DOM is not ready when the component is connected
			if (document.readyState === 'loading') {
				document.addEventListener('DOMContentLoaded', this.handleDOMContentLoaded);
			} else {
				this.populateFormInputs();
			}

			// Populate inputs after cart drawer finishes rendering
			document.addEventListener('td-cart-drawer-rendered', this.handleCartDrawerRendered);
		}

		disconnectedCallback() {
			document.removeEventListener('DOMContentLoaded', this.handleDOMContentLoaded);
			document.removeEventListener('td-cart-drawer-rendered', this.handleCartDrawerRendered);
		}

		/**
		 * Checks if any UTM parameters are present in the URL
		 * @returns {boolean}
		 */
		hasUTMParameters() {
			return this.UTM_PARAMS.some(param => this.urlParams.has(param));
		}

		/**
		 * Clears all stored UTM values from sessionStorage
		 */
		clearStoredUTMs() {
			this.UTM_PARAMS.forEach(param => {
				sessionStorage.removeItem(`${this.UTM_STORAGE_PREFIX}${param}`);
			});
		}

		/**
		 * Captures UTM parameters from URL and stores them in session storage
		 * Clears existing UTM values from sessionStorage to prevent mixed attribution
		 */
		storeUTMParameters() {
			try {
				if (!this.hasUTMParameters()) return;
				this.clearStoredUTMs();

				this.UTM_PARAMS.forEach(param => {
					const value = this.urlParams.get(param);
					if (value) {
						sessionStorage.setItem(`${this.UTM_STORAGE_PREFIX}${param}`, value);
					}
				});
			} catch (error) {
				console.error('[TD UTM Tracking] Error storing UTM parameters:', error);
			}
		}

		/**
		 * Populates hidden form inputs with stored UTM values
		 * Looks for inputs with id pattern: utm-{param}-input (e.g., utm-source-input)
		 */
		populateFormInputs() {
			try {
				this.UTM_PARAMS.forEach(param => {
					const storedValue = sessionStorage.getItem(`${this.UTM_STORAGE_PREFIX}${param}`);
					const inputId = param.replace('_', '-') + '-input';
					const inputField = document.getElementById(inputId);
					
					if (storedValue && inputField) {
						inputField.value = storedValue;
					}
				});
			} catch (error) {
				console.error('[TD UTM Tracking] Error populating form inputs:', error);
			}
		}
	}

	customElements.define('td-utm-tracking', TdUtmTracking);
}
